import { useEffect, useMemo, useState, type ComponentProps, type ReactNode } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import Navbar from "@/components/Navbar";
import { cn } from "@/lib/utils";

type NavbarProps = ComponentProps<typeof Navbar>;

export type HeroSlide = {
  id?: string | number;
  image: string;
  backgroundColor?: string;
  title?: string;
  subtitle?: string;
  text?: string;
  onView?: () => void;
  onClick?: () => void;
};

interface TopHeroTemplateProps {
  /** Imagem única (modo legado) */
  backgroundImage?: string;
  /** Slides para modo carrossel */
  slides?: HeroSlide[];
  /** Tempo (ms) entre cada slide; default 6000ms */
  autoplayMs?: number;
  backgroundColor?: string;
  navbarProps?: NavbarProps;
  children: ReactNode;
  sectionClassName?: string;
  contentClassName?: string;
  overlayClassName?: string;
  /** Renderiza um bloco textual baseado no slide atual antes do `children` */
  renderSlideContent?: (slide: HeroSlide, index: number) => ReactNode;
}

const DEFAULT_OVERLAY = "bg-gradient-to-b from-black/35 via-black/15 to-transparent";

/**
 * Template único para topo com banner + header sobreposto.
 * - Aceita uma imagem única (`backgroundImage`) ou um carrossel de slides (`slides`).
 * - Sem dissolução (fade) na borda inferior — banner termina nítido.
 * - Header sobreposto com pointer-events controlados.
 */
export default function TopHeroTemplate({
  backgroundImage,
  slides,
  autoplayMs = 6000,
  backgroundColor = "transparent",
  navbarProps,
  children,
  sectionClassName,
  contentClassName,
  overlayClassName,
  renderSlideContent,
}: TopHeroTemplateProps) {
  const effectiveSlides = useMemo<HeroSlide[]>(() => {
    if (slides && slides.length > 0) return slides;
    if (backgroundImage) {
      return [{ image: backgroundImage, backgroundColor }];
    }
    return [];
  }, [slides, backgroundImage, backgroundColor]);

  const [activeIndex, setActiveIndex] = useState(0);
  const isCarousel = effectiveSlides.length > 1;

  // Reset active index when slides change
  useEffect(() => {
    setActiveIndex(0);
  }, [effectiveSlides.length]);

  // Autoplay
  useEffect(() => {
    if (!isCarousel) return;
    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % effectiveSlides.length);
    }, Math.max(2500, autoplayMs));
    return () => clearInterval(interval);
  }, [isCarousel, effectiveSlides.length, autoplayMs]);

  // Notify view of active slide
  useEffect(() => {
    const slide = effectiveSlides[activeIndex];
    slide?.onView?.();
  }, [activeIndex, effectiveSlides]);

  const goToPrev = () => {
    setActiveIndex((prev) => (prev - 1 + effectiveSlides.length) % effectiveSlides.length);
  };
  const goToNext = () => {
    setActiveIndex((prev) => (prev + 1) % effectiveSlides.length);
  };

  const currentSlide = effectiveSlides[activeIndex];
  const slideClickable = Boolean(currentSlide?.onClick);

  return (
    <section
      className={cn(
        // pt-* reserva espaço para o header sobreposto, evitando que o conteúdo
        // (título/SearchBar) seja coberto em telas mais altas/estreitas (mobile).
        "relative h-[58vh] min-h-[560px] sm:min-h-[540px] flex flex-col justify-end pt-24 sm:pt-28 md:pt-32 pb-10 overflow-hidden",
        sectionClassName,
      )}
    >
      {/* Slides (background) */}
      <div className="absolute inset-0">
        {effectiveSlides.map((slide, index) => (
          <div
            key={slide.id ?? `${slide.image}-${index}`}
            className={cn(
              "absolute inset-0 bg-cover bg-center transition-opacity duration-700 ease-out",
              index === activeIndex ? "opacity-100 z-10" : "opacity-0 z-0",
              slideClickable && index === activeIndex && "cursor-pointer",
            )}
            style={{
              backgroundImage: `url(${slide.image})`,
              backgroundColor: slide.backgroundColor || backgroundColor,
            }}
            role={slide.onClick && index === activeIndex ? "button" : undefined}
            tabIndex={slide.onClick && index === activeIndex ? 0 : -1}
            onClick={index === activeIndex ? slide.onClick : undefined}
            onKeyDown={
              index === activeIndex && slide.onClick
                ? (event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      slide.onClick?.();
                    }
                  }
                : undefined
            }
            aria-hidden={index !== activeIndex}
          />
        ))}
        <div
          className={cn(
            "absolute inset-0 z-20 pointer-events-none",
            overlayClassName ?? DEFAULT_OVERLAY,
          )}
        />
      </div>

      {/* Header overlay */}
      <div className="pointer-events-none absolute inset-x-0 top-0 z-50 px-3 pt-3 sm:px-4 sm:pt-4 md:pt-5">
        <div className="pointer-events-auto mx-auto max-w-7xl relative [&_header]:sticky [&_header]:top-auto [&_header]:border-b-0 [&_header]:bg-transparent [&_header]:supports-[backdrop-filter]:bg-transparent [&_header]:backdrop-blur-0 [&_.container]:min-h-14 [&_.container]:px-2 sm:[&_.container]:px-4">
          <Navbar {...navbarProps} />
        </div>
      </div>

      {/* Carousel controls */}
      {isCarousel && (
        <>
          <button
            type="button"
            onClick={goToPrev}
            aria-label="Banner anterior"
            className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 z-40 size-10 sm:size-11 rounded-full bg-white/15 hover:bg-white/30 backdrop-blur-md border border-white/20 text-white grid place-items-center transition-colors"
          >
            <ChevronLeft className="size-5" />
          </button>
          <button
            type="button"
            onClick={goToNext}
            aria-label="Próximo banner"
            className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 z-40 size-10 sm:size-11 rounded-full bg-white/15 hover:bg-white/30 backdrop-blur-md border border-white/20 text-white grid place-items-center transition-colors"
          >
            <ChevronRight className="size-5" />
          </button>

          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-40 flex items-center gap-2">
            {effectiveSlides.map((slide, index) => (
              <button
                key={slide.id ?? `dot-${index}`}
                type="button"
                aria-label={`Ir para banner ${index + 1}`}
                aria-current={index === activeIndex}
                onClick={() => setActiveIndex(index)}
                className={cn(
                  "h-1.5 rounded-full transition-all duration-300",
                  index === activeIndex
                    ? "w-8 bg-white"
                    : "w-1.5 bg-white/50 hover:bg-white/80",
                )}
              />
            ))}
          </div>
        </>
      )}

      {/* Content — alinhado ao fundo do hero pelo `flex flex-col justify-end` da section */}
      <div
        className={cn(
          "relative z-30 container mx-auto px-4 text-center",
          contentClassName,
        )}
      >
        {currentSlide && renderSlideContent ? renderSlideContent(currentSlide, activeIndex) : null}
        {children}
      </div>
    </section>
  );
}
